import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from '../utils/axios';
import useAuth from './useAuth';

// ----------------------------------------------------------------------

/**
 * Counts for nav items that carry a `badgeSource`.
 *
 * A worklist someone else fills is invisible until you happen to open the
 * page. The badge is the only thing telling accounts a deal is sitting there,
 * so it is fetched for the nav itself rather than by the page it points at.
 *
 * Refreshed on navigation rather than on a timer: the moment the number
 * changes for the person looking at it is right after they issue an invoice,
 * and that is a route change. The last answer is cached at module level so
 * moving between pages re-uses it instead of re-fetching and flickering.
 */

const MIN_INTERVAL_MS = 30 * 1000;

const SOURCES = {
  // Signed by sales, not yet invoiced by accounts.
  billingHandoffs: {
    role: 'org',
    url: '/api/v1/tenancy/admin/billing-handoffs/summary/',
    permissions: ['billing.view', 'subscriptions.manage'],
    read: (data) => data?.open || 0,
  },
  // Releases sitting still because of the person looking at the nav: the
  // merge queue for the founder, the review queue for the product manager.
  // The server decides which of those `mine` means, so the nav never has to
  // know who is a founder.
  deployQueue: {
    role: 'boss',
    url: '/api/v1/deskos-billing/build/deploys/queue/badge/',
    read: (data) => data?.mine || 0,
  },
};

let cache = {};
let fetchedAt = 0;

export default function useNavBadges() {
  const { user, hasPermission } = useAuth();
  const { pathname } = useLocation();
  const [counts, setCounts] = useState(cache);

  const role = user?.role;

  useEffect(() => {
    if (!role) return undefined;
    if (Date.now() - fetchedAt < MIN_INTERVAL_MS) return undefined;

    const wanted = Object.entries(SOURCES).filter(
      ([, source]) =>
        source.role === role &&
        (!source.permissions || source.permissions.some((code) => hasPermission?.(code)))
    );
    if (!wanted.length) return undefined;

    let cancelled = false;
    fetchedAt = Date.now();

    Promise.all(
      wanted.map(async ([key, source]) => {
        try {
          const { data } = await axios.get(source.url);
          return [key, source.read(data)];
        } catch {
          // A badge is not worth an error state. Nothing shows.
          return [key, 0];
        }
      })
    ).then((results) => {
      cache = Object.fromEntries(results);
      if (!cancelled) setCounts(cache);
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, pathname]);

  return counts;
}
